// §preview: registry of the omzo-style presentation layer for every intake
// flow, served at /preview/<key>. Each flow definition supplies stage names,
// per-question headline/hint/illustration/option icons, and the marketing
// interstitials shown between questions. The question ORDER and content come
// from each flow's data file unchanged; nothing here is submitted.
//
// Shape of a flow definition (see ./glp1.js for the fullest example):
//   { key, route, title, stages:[5 names], stageDefaults:{stage:{headline,image}},
//     meta:{ [questionId]: {stage, headline:[plain, accent], hint?, image?, optionIcons?} },
//     interstitials:[ {id, after:<questionId>, kind, ...content} ] }
// Images are public/preview/<image>.svg where image is "common/<name>" or
// "<flow>/<name>".
import glp1 from './glp1';
import branded from './branded';
import sermorelin from './sermorelin';
import antiAging from './antiAging';
import trt from './trt';
import wsh from './wsh';
import msh from './msh';
import labPanel from './labPanel';
import labAnalysis from './labAnalysis';
import advancedPeptide from './advancedPeptide';
import advancedWeightLoss from './advancedWeightLoss';
import advancedLongevity from './advancedLongevity';

export const PREVIEW_FLOWS = { glp1, branded, sermorelin, antiAging, trt, wsh, msh, labPanel, labAnalysis, advancedPeptide, advancedWeightLoss, advancedLongevity };

export function metaFor(def, id) {
  const m = def && def.meta && def.meta[id];
  if (m) return { hint: null, optionIcons: null, image: null, ...m };
  const stage = (def && def.fallbackStage) || (def && def.stages[2]);
  const d = (def && def.stageDefaults && def.stageDefaults[stage]) || {};
  return { stage, hint: null, optionIcons: null, image: null, headline: null, ...d };
}

export function stageIndexFor(def, id) {
  if (!def) return 0;
  return Math.max(0, def.stages.indexOf(metaFor(def, id).stage));
}

export const imageSrc = (name) => (name ? `/preview/${name}.svg` : null);

// Insert the flow's interstitials right after the question named in `after`.
// The question order itself is untouched; a slide whose anchor isn't in the
// current branch is simply not shown. Interstitial steps collect no answer.
export function insertInterstitials(def, flow) {
  if (!def || !Array.isArray(flow) || !flow.length || !def.interstitials) return flow;
  const out = [];
  flow.forEach((q) => {
    out.push(q);
    def.interstitials.forEach((ix) => {
      if (q && q.id === ix.after) out.push({ ...ix, type: 'preview-interstitial' });
    });
  });
  return out;
}

// Clone a question with per-option emoji icons from meta (QuestionStep renders
// `option.icon` when showIcons is on). Leaves the source question untouched.
export function withOptionIcons(question, meta) {
  if (!question || !meta || !meta.optionIcons || !Array.isArray(question.options)) return question;
  return {
    ...question,
    options: question.options.map((o) => {
      const v = typeof o === 'string' ? o : o.value;
      const icon = meta.optionIcons[v];
      if (!icon) return o;
      return typeof o === 'string' ? { label: o, value: o, icon } : { ...o, icon };
    }),
  };
}
